// src/pages/CheckInKioskPage.js — self-service check-in for iPads at the door
// [marker: back-tags v1]
// Guests type their name, tap it, confirm, and their name tag prints. A staff member signs the
// iPad in once; the kiosk then runs full screen. ⚙ (top corner) holds the settings and the exit.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { BadgeView, printBadges, defaultDesign, sizeOf } from '../components/Badge';

const NAVY = '#1a3a5c'; const GOLD = '#c8a84b';
const load = (k, d) => { try { const v = localStorage.getItem(`kiosk:${k}`); return v === null ? d : JSON.parse(v); } catch { return d; } };
const keep = (k, v) => { try { localStorage.setItem(`kiosk:${k}`, JSON.stringify(v)); } catch {} };

export default function CheckInKioskPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [design, setDesign] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [step, setStep] = useState('search'); // search | confirm | done | already | error
  const [person, setPerson] = useState(null);
  const [msg, setMsg] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [autoPrint, setAutoPrint] = useState(() => load('autoPrint', true));
  const [showCompany, setShowCompany] = useState(() => load('showCompany', true));
  const [count, setCount] = useState(null);
  const inputRef = useRef(null);
  const resetTimer = useRef(null);

  useEffect(() => {
    api.get(`/badges/design?eventId=${eventId}`).then(r => { setInfo(r.data); setDesign(r.data.design || defaultDesign(r.data.orgLogoUrl)); })
      .catch(() => setInfo({ error: true }));
  }, [eventId]);
  const refreshCount = useCallback(() => {
    api.get(`/badges/events/${eventId}/people`).then(r => setCount({ total: r.data.length, in: r.data.filter(p => p.checked_in).length })).catch(() => {});
  }, [eventId]);
  useEffect(() => { refreshCount(); }, [refreshCount]);

  const reset = useCallback(() => {
    clearTimeout(resetTimer.current);
    setStep('search'); setQ(''); setResults([]); setPerson(null); setMsg('');
    setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
  }, []);
  const resetSoon = (ms = 7000) => { clearTimeout(resetTimer.current); resetTimer.current = setTimeout(reset, ms); };

  // search as they type
  useEffect(() => {
    if (step !== 'search') return undefined;
    const term = q.trim();
    if (term.length < 2) { setResults([]); return undefined; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`/badges/events/${eventId}/people?q=${encodeURIComponent(term)}`).then(r => setResults(r.data || []))
        .catch(() => setResults([])).finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, step, eventId]);

  const print = async (p) => {
    try { await printBadges(design, [p], info.event); await api.post('/badges/printed', { ticketIds: [p.ticket_id] }); } catch { /* the tag can be reprinted */ }
  };
  const checkIn = async () => {
    const p = person;
    try {
      await api.post(`/tickets/${p.ticket_id}/check-in`);
      setStep('done'); refreshCount();
      if (autoPrint) await print(p);
      resetSoon(6000);
    } catch (err) {
      if (/already/i.test(err.response?.data?.error || '')) { setStep('already'); resetSoon(12000); }
      else { setStep('error'); setMsg(err.response?.data?.error || 'Something went wrong.'); resetSoon(10000); }
    }
  };

  if (!info || !design) return <Screen><div style={{ fontSize: '1.4rem' }}>Loading…</div></Screen>;
  if (info.error || !info.event) return <Screen><div style={{ fontSize: '1.4rem' }}>This event couldn’t be loaded.</div><button style={btnLight} onClick={() => navigate(-1)}>Go back</button></Screen>;
  const ev = info.event;
  const { w } = sizeOf(design);

  return (
    <Screen>
      <button onClick={() => setSettingsOpen(true)} aria-label="Settings" style={{ position: 'fixed', top: 14, right: 16, background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: 26, cursor: 'pointer' }}>⚙</button>
      <div style={{ textAlign: 'center', marginBottom: 26 }}>
        {design.logo?.src && <img src={design.logo.src} alt="" style={{ maxHeight: 70, maxWidth: 220, marginBottom: 10, background: 'white', borderRadius: 8, padding: 6 }} />}
        <div style={{ color: GOLD, letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: '0.9rem', fontWeight: 700 }}>Welcome to</div>
        <h1 style={{ margin: '4px 0 0', fontFamily: 'Georgia, serif', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>{ev.name}</h1>
      </div>

      {step === 'search' && (
        <div style={{ width: '100%', maxWidth: 640 }}>
          <input ref={inputRef} autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Type your last name to check in"
                 autoComplete="off" autoCorrect="off" spellCheck={false}
                 style={{ width: '100%', boxSizing: 'border-box', fontSize: '1.6rem', padding: '18px 22px', borderRadius: 14, border: `3px solid ${GOLD}`, outline: 'none', color: '#111' }} />
          <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
            {results.map(p => (
              <button key={p.ticket_id} onClick={() => { setPerson(p); setStep('confirm'); resetSoon(30000); }}
                      style={{ textAlign: 'left', background: 'white', color: NAVY, border: 'none', borderRadius: 12, padding: '16px 20px', fontSize: '1.35rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <span><strong>{p.first_name} {p.last_name}</strong>{showCompany && p.company ? <span style={{ color: '#6b7280', fontSize: '1rem' }}> · {p.company}</span> : null}</span>
                {p.checked_in ? <span style={{ fontSize: '0.9rem', color: '#059669' }}>✓ checked in</span> : <span style={{ fontSize: '1.4rem' }}>›</span>}
              </button>
            ))}
            {q.trim().length >= 2 && !searching && results.length === 0 && (
              <div style={{ textAlign: 'center', padding: 18, fontSize: '1.2rem', opacity: 0.9 }}>
                We couldn’t find that name. Try your first name, or please see the front desk — we’ll get you right in.
              </div>
            )}
          </div>
        </div>
      )}

      {step === 'confirm' && person && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: 14 }}>Is this you?</div>
          <div style={{ display: 'inline-block', marginBottom: 20 }}>
            <BadgeView design={design} person={person} event={ev} scale={Math.min(1.5, 420 / (w * 96))} />
          </div>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center' }}>
            <button style={btnGold} onClick={checkIn}>{person.checked_in ? 'Yes — print my tag' : 'Yes, check me in'}</button>
            <button style={btnLight} onClick={reset}>No, go back</button>
          </div>
        </div>
      )}

      {step === 'done' && person && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '4rem' }}>✓</div>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>Welcome, {person.first_name}!</div>
          <div style={{ fontSize: '1.2rem', marginTop: 8, opacity: 0.9 }}>{autoPrint ? 'Your name tag is printing.' : 'You’re checked in. Enjoy the event!'}</div>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', marginTop: 26 }}>
            {!autoPrint && <button style={btnGold} onClick={() => print(person)}>Print my name tag</button>}
            <button style={btnLight} onClick={reset}>Done</button>
          </div>
        </div>
      )}

      {step === 'already' && person && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>You’re already checked in, {person.first_name}.</div>
          <div style={{ fontSize: '1.15rem', marginTop: 8, opacity: 0.9 }}>Need another name tag?</div>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', marginTop: 24 }}>
            <button style={btnGold} onClick={async () => { await print(person); resetSoon(4000); }}>Reprint my name tag</button>
            <button style={btnLight} onClick={reset}>Done</button>
          </div>
        </div>
      )}

      {step === 'error' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>Sorry — that didn’t work.</div>
          <div style={{ marginTop: 8 }}>{msg} Please see the front desk.</div>
          <button style={{ ...btnLight, marginTop: 20 }} onClick={reset}>Start over</button>
        </div>
      )}

      {settingsOpen && (
        <div onClick={() => setSettingsOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', color: '#111', borderRadius: 14, padding: 24, width: 'min(92vw, 440px)' }}>
            <h2 style={{ marginTop: 0 }}>Kiosk settings</h2>
            {count && <p style={{ marginTop: 0, color: '#4b5563' }}>{count.in} of {count.total} checked in so far.</p>}
            <label style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '10px 0' }}>
              <input type="checkbox" checked={autoPrint} onChange={e => { setAutoPrint(e.target.checked); keep('autoPrint', e.target.checked); }} />
              Print the name tag right after check-in
            </label>
            <label style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '10px 0' }}>
              <input type="checkbox" checked={showCompany} onChange={e => { setShowCompany(e.target.checked); keep('showCompany', e.target.checked); }} />
              Show company names in the search results
            </label>
            <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              Tip: on the iPad, use Guided Access (Settings → Accessibility) to keep guests inside this screen. The first tag opens the iPad’s print window —
              pick the label printer once and it will be remembered.
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={() => { if (window.confirm('Leave the kiosk?')) navigate(`/events/${eventId}`); }}>Exit kiosk</button>
              <button className="btn btn-primary" onClick={() => setSettingsOpen(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </Screen>
  );
}

function Screen({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(160deg, ${NAVY} 0%, #10263d 100%)`, color: 'white', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'flex-start', padding: '7vh 20px 40px', boxSizing: 'border-box', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif' }}>
      {children}
    </div>
  );
}
const btnGold = { background: GOLD, color: NAVY, border: 'none', borderRadius: 12, padding: '16px 28px', fontSize: '1.3rem', fontWeight: 700, cursor: 'pointer' };
const btnLight = { background: 'rgba(255,255,255,0.12)', color: 'white', border: '2px solid rgba(255,255,255,0.5)', borderRadius: 12, padding: '14px 26px', fontSize: '1.2rem', cursor: 'pointer' };
